/**
 * A user with exactly one organization is connected to it without having to
 * click it on the picker (Jeff, 2026-10-27). That happens at most once per
 * sign-in in a tab: if the picker comes up again in the same sign-in, something
 * sent the user back (a failed connect, Clear Current Connection, a switch),
 * and connecting again would only bounce them round in a loop. The second time
 * the picker shows the list.
 *
 * The claim is kept in sessionStorage (it must survive the picker unmounting
 * and remounting), or for the page load when storage is unavailable.
 * Log Out clears it (clearSignedInSession), and a new Keycloak session has a
 * new session_state, so the next sign-in gets its own automatic connect.
 */
export const AUTO_CONNECT_KEY = 'dietech_single_org_auto_connect';

let claimedInMemory: string | null = null;

interface SignedInUser {
  session_state?: string | null;
  profile?: { sub?: string; sid?: string; email?: string; preferred_username?: string };
}

/** Who is signed in and which sign-in this is; empty when there is no user. */
export function signInKey(user: SignedInUser | null | undefined): string {
  if (!user) return '';
  const who = user.profile?.sub || user.profile?.email || user.profile?.preferred_username || '';
  if (!who) return '';
  return `${who}|${user.session_state || user.profile?.sid || ''}`;
}

/**
 * True the first time it is called for this sign-in in this tab, false after.
 * Claims before the connect is attempted, so a connect that fails is never
 * tried again automatically.
 */
export function claimAutoConnect(key: string): boolean {
  if (!key) return false;
  try {
    if (sessionStorage.getItem(AUTO_CONNECT_KEY) === key) return false;
    sessionStorage.setItem(AUTO_CONNECT_KEY, key);
    return true;
  } catch {
    // Storage unavailable: remember the claim for this page load instead.
    if (claimedInMemory === key) return false;
    claimedInMemory = key;
    return true;
  }
}
